import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, TextField, Typography } from "@mui/material";
import { Badge, ChoiceToggle, FieldWrapper, PageHeader, StepActions, UploadCard } from "@components/ui_v2";
import { PersonaExpediente } from "@interfaces/empresasWizard";
import { DOCUMENTS_CATALOG, PATHS_EXPEDIENTE_V2 } from "@helpers/constantes";

const PERSONAS_INICIALES: PersonaExpediente[] = [
  { id: "rl", nombre: "", rolesLabel: "Representante Legal", identidadValidada: false },
  { id: "am", nombre: "", rolesLabel: "Accionista Mayoritario · Aval", identidadValidada: false },
  { id: "pr", nombre: "", rolesLabel: "Propietario Real", identidadValidada: false },
];

export const ExpPersonas = () => {
  const navigate = useNavigate();
  const [personas, setPersonas] = useState<PersonaExpediente[]>(PERSONAS_INICIALES);
  const [active, setActive] = useState(PERSONAS_INICIALES[0].id);

  const onBack = () => navigate(PATHS_EXPEDIENTE_V2.referencias.path);
  const onNext = () => navigate(PATHS_EXPEDIENTE_V2.validacionIdentidad.path);

  const personIdx = personas.findIndex((p) => p.id === active);
  const person = personas[personIdx];

  const updatePerson = (patch: Partial<PersonaExpediente>) => {
    setPersonas((prev) => prev.map((p, i) => (i === personIdx ? { ...p, ...patch } : p)));
  };

  const allDone = personas.every((p) => p.nombre && p.documento);

  return (
    <Box>
      <PageHeader
        eyebrow="Expediente digital"
        title="Personas relacionadas"
        subtitle="Completa la información pendiente de las personas que participan en el expediente, según los roles asignados."
      />

      <Box sx={{ mb: 2.5 }}>
        <ChoiceToggle
          value={active}
          onChange={(v) => setActive(v as string)}
          options={personas.map((p) => ({
            value: p.id,
            label: `${p.nombre || p.rolesLabel}${p.nombre && p.documento ? " ✓" : ""}`,
          }))}
        />
      </Box>

      <Box
        sx={{
          bgcolor: "background.paper",
          border: "1px solid",
          borderColor: "background.paper",
          borderRadius: 2,
          p: 3,
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="body2" fontWeight={700} color="primary.dark">
            {person.rolesLabel}
          </Typography>
          <Badge tone={person.nombre && person.documento ? "ok" : "warn"}>
            {person.nombre && person.documento ? "Completo" : "Pendiente"}
          </Badge>
        </Box>
        <FieldWrapper label="Nombre completo" full>
          <TextField
            autoComplete="off"
            fullWidth
            value={person.nombre}
            onChange={(e) => updatePerson({ nombre: e.target.value })}
            placeholder="Nombre(s) y apellidos"
          />
        </FieldWrapper>
      </Box>

      <Box sx={{ mt: 1.5 }}>
        <UploadCard
          title="Documentos de identificación"
          sub="Identificación oficial vigente y comprobante de domicilio."
          fileCode={DOCUMENTS_CATALOG.identificacion_oficial.code}
          document={person.documento}
          onDocumentChange={(documento) => updatePerson({ documento })}
        />
      </Box>

      <StepActions onBack={onBack} onNext={onNext} nextLabel="Validar expediente" nextDisabled={!allDone} />
    </Box>
  );
};
